/**
 * CorporationServiceUtils.js
 * 
 * Utility functions for checking corporation service ON/OFF status.
 * These functions check limitValue, corporationSetting, and inviterSetting
 * to determine if a service is enabled for a corporation.
 */

import Utils from './utils';
import Constant from '../constant/Constant';

// ============================================================================
// AI Chat Service
// ============================================================================

/**
 * Check if corporation has AI Chat service enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isCorporationOnAI = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if (Utils.isGuestContractType(corporationDetail)) {
        return corporationDetail?.inviterUsingChatAI === Constant.STATUS_SERVICE_PERMISSION.ON;
    }

    return (
        corporationDetail?.limitValue?.usingChatAI === Constant.STATUS_SERVICE_PERMISSION.ON &&
        corporationDetail?.limitValue?.chatAI === Constant.STATUS_SERVICE_PERMISSION.ON
    );
};

// ============================================================================
// Knowledge Service
// ============================================================================

/**
 * Check if corporation has Knowledge service enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isCorporationOnKnowledge = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if (Utils.isGuestContractType(corporationDetail)) {
        const inviterSetting = Utils.convertJSONDataToObject(corporationDetail.inviterSetting);
        const inviterDetail = Utils.convertJSONDataToObject(corporationDetail?.inviterDetail);
        return (
            inviterSetting?.usingKnowledgeService === Constant.ACTIVE_FLAG.ACTIVE ||
            inviterDetail?.knowledgeService === Constant.ACTIVE_FLAG.ACTIVE
        );
    }

    return corporationDetail?.knowledgeService === Constant.ACTIVE_FLAG.ACTIVE;
};

// ============================================================================
// AI Auto Service
// ============================================================================

/**
 * Check if corporation has AI Auto service enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isCorporationOnAIAuto = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if (corporationDetail.contractType === Constant.CORPORATION_CONTRACT_TYPE.GUEST) {
        return false;
    }

    const isUsingAIAuto = corporationDetail.contractType === Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK
        ? (
            Utils.convertJSONDataToObject(corporationDetail.inviterSetting)?.usingAIAuto === Constant.STATUS_SERVICE_PERMISSION.ON &&
            Utils.convertJSONDataToObject(corporationDetail.inviterSetting)?.aiAuto === Constant.STATUS_SERVICE_PERMISSION.ON
        )
        : (
            corporationDetail?.limitValue?.usingAIAuto === Constant.STATUS_SERVICE_PERMISSION.ON &&
            corporationDetail?.limitValue?.aiAuto === Constant.STATUS_SERVICE_PERMISSION.ON
        );

    return isUsingAIAuto && isCorporationOnKnowledge(corporationDetail);
};

// ============================================================================
// SD Auto Service
// ============================================================================

/**
 * Check if corporation has SD Auto service enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isCorporationOnSDAuto = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if (corporationDetail.contractType === Constant.CORPORATION_CONTRACT_TYPE.GUEST) {
        return false;
    }

    const isUsingSDAuto = corporationDetail.contractType === Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK
        ? (
            Utils.convertJSONDataToObject(corporationDetail.inviterSetting)?.usingSDAuto === Constant.STATUS_SERVICE_PERMISSION.ON &&
            Utils.convertJSONDataToObject(corporationDetail.inviterSetting)?.sdAuto === Constant.STATUS_SERVICE_PERMISSION.ON
        )
        : (
            corporationDetail?.limitValue?.usingSDAuto === Constant.STATUS_SERVICE_PERMISSION.ON &&
            corporationDetail?.limitValue?.sdAuto === Constant.STATUS_SERVICE_PERMISSION.ON
        );

    return isUsingSDAuto && isCorporationOnKnowledge(corporationDetail);
};

// ============================================================================
// KBoard Prompt Service
// ============================================================================

/**
 * Check if corporation has KBoard Prompt service enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isCorporationOnKBoardPrompt = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;
    if (!isCorporationOnKnowledge(corporationDetail)) return false;

    if (Utils.isGuestContractType(corporationDetail)) {
        const inviterSetting = Utils.convertJSONDataToObject(corporationDetail?.inviterSetting);
        return (
            inviterSetting?.kBoard === Constant.STATUS_SERVICE_PERMISSION.ON &&
            inviterSetting?.usingKBoard === Constant.STATUS_SERVICE_PERMISSION.ON
        );
    }

    return (
        corporationDetail?.limitValue?.usingKBoard === Constant.STATUS_SERVICE_PERMISSION.ON &&
        corporationDetail?.limitValue?.kBoard === Constant.STATUS_SERVICE_PERMISSION.ON
    );
};

// ============================================================================
// Telephone Agent Service
// ============================================================================

/**
 * Check if telephone agent is enabled in organization settings
 * Telephone agent depends on AI Auto: it is only available while AI Auto is ON.
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isEnableSettingTelephoneAgentInOrg = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if ([Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(corporationDetail.contractType)) {
        const inviterSetting = Utils.convertJSONDataToObject(corporationDetail.inviterSetting);
        return (
            inviterSetting.usingTelephoneAgent === Constant.STATUS_SERVICE_PERMISSION.ON &&
            inviterSetting.telephoneAgent === Constant.STATUS_SERVICE_PERMISSION.ON &&
            inviterSetting.usingAIAuto === Constant.STATUS_SERVICE_PERMISSION.ON &&
            inviterSetting.aiAuto === Constant.STATUS_SERVICE_PERMISSION.ON
        );
    }

    const hasTelephoneAgentPermission = corporationDetail?.limitValue?.telephoneAgent === Constant.STATUS_SERVICE_PERMISSION.ON;
    const hasTelephoneAgentSetting = corporationDetail?.limitValue?.usingTelephoneAgent === Constant.STATUS_SERVICE_PERMISSION.ON;
    const hasAIAutoOn =
        corporationDetail?.limitValue?.aiAuto === Constant.STATUS_SERVICE_PERMISSION.ON &&
        corporationDetail?.limitValue?.usingAIAuto === Constant.STATUS_SERVICE_PERMISSION.ON;

    return hasTelephoneAgentPermission && hasTelephoneAgentSetting && hasAIAutoOn;
};

/**
 * Check if telephone mode is enabled for corporation
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isEnableUsingTelephoneMode = (corporationDetail) => {
    return isEnableSettingTelephoneAgentInOrg(corporationDetail);
};

/**
 * Check if project has telephone support enabled
 * @param {Object} projectDetail - Project detail object
 * @returns {boolean}
 */
const isProjectOnTelephoneSupport = (projectDetail) => {
    if (!projectDetail || !projectDetail.projectSetting) {
        return false;
    }

    let projectSetting = {};
    try {
        projectSetting = typeof projectDetail.projectSetting === 'string'
            ? Utils.convertJSONDataToObject(projectDetail.projectSetting)
            : projectDetail.projectSetting;
    } catch (error) {
        console.error('Error parsing projectSetting:', error);
        return false;
    }

    return projectSetting.telephoneAgent === Constant.STATUS_SERVICE_PERMISSION.ON
        || projectSetting.telephoneAgent === 1;
};

// ============================================================================
// Email Response Service
// ============================================================================

/**
 * Check if email ticket creation mode is enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @param {Object} projectInuseDetail - Project detail object
 * @returns {boolean}
 */
const isEnableCreateEmailTicketMode = (corporationDetail, projectInuseDetail) => {
    if (Utils.isEmptyObject(corporationDetail) || Utils.isEmptyObject(projectInuseDetail)) return false;

    const mailSetting = projectInuseDetail?.mailSetting ? Utils.convertJSONDataToObject(projectInuseDetail?.mailSetting) : {};
    const isProjectOnEmailTicketMode = mailSetting?.isEmailResponseSettingFunction;

    let isCorporationOnEmailTicketMode = false;
    if ([Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(corporationDetail.contractType)) {
        const inviterSetting = Utils.convertJSONDataToObject(corporationDetail.inviterSetting);
        isCorporationOnEmailTicketMode = inviterSetting.emailResponseFunction;
    } else {
        isCorporationOnEmailTicketMode = corporationDetail?.limitValue?.emailResponseFunction;
    }

    return isCorporationOnEmailTicketMode && isProjectOnEmailTicketMode && Utils.isPublicDesk(projectInuseDetail);
};

// ============================================================================
// MFA (Multi-Factor Authentication) Service
// ============================================================================

/**
 * Check if corporation has MFA enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isCorporationOnMfa = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if ([
        Constant.CORPORATION_CONTRACT_TYPE.GUEST,
        Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK
    ].includes(corporationDetail.contractType)) {
        return false;
    }

    return (
        corporationDetail?.corporationSetting?.usingMultiAuth === true &&
        corporationDetail?.limitValue?.multiAuth === Constant.STATUS_SERVICE_PERMISSION.ON
    );
};

// ============================================================================
// Convert PDF Service
// ============================================================================

/**
 * Check if corporation has Convert PDF service enabled
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const isCorpOnConvertPdf = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    return (
        corporationDetail?.limitValue?.convertPDF === Constant.STATUS_SERVICE_PERMISSION.ON ||
        corporationDetail?.inviterDetail?.limitValue?.convertPDF === Constant.STATUS_SERVICE_PERMISSION.ON
    );
};

// ============================================================================
// Synonym Search Service
// ============================================================================

/**
 * Check if synonym search is enabled for corporation and project
 * @param {Object} corporationDetail - Corporation detail object
 * @param {Object} projectInuseDetail - Project detail object
 * @returns {boolean}
 */
const isOnSearchSynonym = (corporationDetail, projectInuseDetail) => {
    if (!corporationDetail || !projectInuseDetail) return false;

    const projectSetting = Utils.convertJSONDataToObject(projectInuseDetail.projectSetting);
    if (!projectSetting || !projectSetting.usingSynonym) return false;

    const corporationLimitValue = corporationDetail.limitValue;
    const inviterSetting = corporationDetail.inviterSetting
        ? Utils.convertJSONDataToObject(corporationDetail.inviterSetting)
        : {};

    return Boolean(
        corporationLimitValue?.synonymSearch ||
        (corporationDetail.contractType === Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK && inviterSetting.synonymSearch)
    );
};

// ============================================================================
// Desk Service
// ============================================================================

/**
 * Check if ticket should be disabled when desk service is off
 * @param {Object} ticket - Ticket detail object
 * @param {Object} corporation - Corporation detail object
 * @returns {boolean}
 */
const isDisabledTicketExternalWhenDeskServiceOff = (ticket, corporation) => {
    if (Utils.isEmptyObject(ticket) || Utils.isEmptyObject(corporation)) return false;

    if (
        corporation.deskService === Constant.STATUS_SERVICE_PERMISSION.OFF &&
        ticket.isExternalTicket &&
        (ticket.ticketType === Constant.TICKET_TYPE.BROADCAST_PARENT ||
            ticket.ticketType === Constant.TICKET_TYPE.DELEGATE)
    ) {
        return true;
    }
    return false;
};

// ============================================================================
// LimitValue Utils
// ============================================================================

/**
 * Custom limitValue before edit organization (remove computed fields)
 * @param {Object} limitValue - LimitValue object
 * @returns {Object}
 */
const customLimitValueBeforeEditOrg = (limitValue) => {
    if (Utils.isEmptyObject(limitValue)) return limitValue;

    let customedLimitValue = { ...limitValue };

    // Remove computed fields
    delete customedLimitValue.numOfB2C;
    delete customedLimitValue.numOfGeneralProjects;
    delete customedLimitValue.numOfQACounters;
    delete customedLimitValue.numOfInternalCustomers;
    delete customedLimitValue.numOfSupportedCounters;
    delete customedLimitValue.numberOfKnowledges;
    delete customedLimitValue.numberOfExternalAPICalls;

    // Remove service flags (managed separately)
    delete customedLimitValue.deskService;
    delete customedLimitValue.knowledgeService;
    delete customedLimitValue.publicService;

    // Convert AWSJSON field to string
    customedLimitValue.lincenseKeySetting = JSON.stringify(customedLimitValue?.lincenseKeySetting);

    if (customedLimitValue?.hasOwnProperty('localMcpEnabled')) {
        if (typeof customedLimitValue.localMcpEnabled !== 'string') {
            customedLimitValue.localMcpEnabled = JSON.stringify(
                customedLimitValue?.localMcpEnabled
            );
        }
    }

    return customedLimitValue;
};

/**
 * Convert BigInt types in limitValue to Number
 * @param {Object} limitValue - LimitValue object
 * @returns {Object}
 */
const convertBigIntTypeInLimitValue = (limitValue) => {
    if (Utils.isEmptyObject(limitValue)) return limitValue;

    return Object.entries({ ...limitValue }).reduce((result, [key, value]) => {
        if (typeof value === 'bigint') {
            result[key] = Number(value);
        } else {
            result[key] = value;
        }
        return result;
    }, {});
};

// ============================================================================
// Knowledge Permission
// ============================================================================

/**
 * Check user permission for knowledge service
 * @param {Object} corpData - Corporation detail object
 * @param {Object} projectData - Project detail object
 * @returns {string} - User permission type
 */
const checkUserPermissionKnowledgeService = (corpData, projectData) => {
    let userPermission = '';
    if (Utils.isEmptyObject(corpData) || Utils.isEmptyObject(projectData)) return userPermission;

    const contractType = corpData.contractType;
    switch (contractType) {
        case Constant.CORPORATION_CONTRACT_TYPE.PREMIUM:
            if (projectData.type === Constant.PROJECT_TYPE.GENERAL) {
                userPermission = Constant.USER_PERMISSION_VIEW_KNOWLEDGE.MEMBER_OF_PROJECT;
            }
            if (projectData.type === Constant.PROJECT_TYPE.BASE) {
                userPermission = Constant.USER_PERMISSION_VIEW_KNOWLEDGE.PERSONAL_PROJECT;
            }
            if (projectData.type === Constant.PROJECT_TYPE.CUSTOMER) {
                userPermission = Constant.USER_PERMISSION_VIEW_KNOWLEDGE.B2C;
            }
            break;
        case Constant.CORPORATION_CONTRACT_TYPE.GUEST:
            userPermission = Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_SUPPORTED;
            break;
        case Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK:
            userPermission = Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_PARTNER;
            break;
        default:
            break;
    }
    return userPermission;
};

// ============================================================================
// Training View
// ============================================================================

/**
 * Check if training view should be shown
 * Only depends on the organization telephone setting, not on the project telephone setting:
 * training calls run over OpenAI WebRTC (browser mic), not over the project telephone line.
 * @param {Object} project - Project detail object
 * @param {Object} corporationDetail - Corporation detail object
 * @returns {boolean}
 */
const shouldShowTrainingView = (project, corporationDetail = null) => {
    if (!project || Utils.isEmptyObject(project)) return false;

    const isDeskEnabled =
        Utils.isPublicDesk(project) ||
        Utils.isPrivateDesk(project) ||
        Utils.isProjectGuestDesk(project);

    if (!isDeskEnabled) return false;

    return isEnableSettingTelephoneAgentInOrg(corporationDetail);
};

/**
 * Check if project has phone support enabled
 * @param {Object} projectDetail - Project detail object
 * @returns {boolean}
 */
const isProjectOnPhoneSupport = (projectDetail) => {
    if (!projectDetail || !projectDetail.projectSetting) return false;

    const projectSetting = Utils.convertJSONDataToObject(projectDetail.projectSetting);
    return projectSetting?.telephoneAgentSetting?.isUsingTelephoneAgent === true;
};

// ============================================================================
// File Translation
// ============================================================================

const isEnableSettingFileTranslation = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if ([Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(corporationDetail.contractType)) {
        const inviterSetting = Utils.convertJSONDataToObject(corporationDetail.inviterSetting);
        return (
            inviterSetting.fileTranslation === Constant.STATUS_SERVICE_PERMISSION.ON &&
            inviterSetting.usingFileTranslation === Constant.STATUS_SERVICE_PERMISSION.ON
        );
    }

    const hasFileTranslationPermission = corporationDetail?.limitValue?.fileTranslation === Constant.STATUS_SERVICE_PERMISSION.ON;
    const hasFileTranslationSetting = corporationDetail?.limitValue?.usingFileTranslation === Constant.STATUS_SERVICE_PERMISSION.ON;

    return hasFileTranslationPermission && hasFileTranslationSetting;
};

// ============================================================================
// Multi-language
// ============================================================================

const isEnableSettingMultiLanguage = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if ([Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(corporationDetail.contractType)) {
        const inviterSetting = Utils.convertJSONDataToObject(corporationDetail.inviterSetting);
        return (
            inviterSetting.multiLanguage === Constant.STATUS_SERVICE_PERMISSION.ON &&
            inviterSetting.usingMultiLanguage === Constant.STATUS_SERVICE_PERMISSION.ON
        );
    }

    const hasMultiLanguagePermission = corporationDetail?.limitValue?.multiLanguage === Constant.STATUS_SERVICE_PERMISSION.ON;
    const hasMultiLanguageSetting = corporationDetail?.limitValue?.usingMultiLanguage === Constant.STATUS_SERVICE_PERMISSION.ON;

    return hasMultiLanguagePermission && hasMultiLanguageSetting;
};

/**
 * Whether the org may open 翻訳管理 (MLT-001-F2 PC-1/PC-2), admin checks excluded.
 * Guest orgs (Project Partner / Project Supported) never manage the dictionary,
 * even though they inherit 多言語対応 from the inviter.
 */
const isEnableTranslationManagement = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    if ([Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(corporationDetail.contractType)) {
        return false;
    }

    return isEnableSettingMultiLanguage(corporationDetail);
};

/**
 * Org that owns the translation dictionary of this corporation.
 * A guest / partner org (GUEST, GUEST_DESK) shares the dictionary of its inviter;
 * any other org owns its own.
 * @returns {string|null}
 */
const resolveDictionaryOrgId = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return null;

    const isGuest = [Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(corporationDetail.contractType);
    return isGuest && corporationDetail.inviter ? corporationDetail.inviter : corporationDetail.id;
};

/**
 * Corporation.translationCacheResetAt of the org that owns the dictionary.
 * A guest / partner org reads its inviter's value from inviterSetting.
 * @returns {string|null}
 */
const getDictionaryCacheResetAt = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return null;

    if (resolveDictionaryOrgId(corporationDetail) !== corporationDetail.id) {
        const inviterSetting = Utils.convertJSONDataToObject(corporationDetail.inviterSetting);
        return inviterSetting?.translationCacheResetAt || null;
    }
    return corporationDetail.translationCacheResetAt || null;
};

// ============================================================================
// MCP Server
// ============================================================================

const isEnableSettingMCPInOrg = (corporationDetail) => {
    if (Utils.isEmptyObject(corporationDetail)) return false;

    // Check contract type is PREMIUM
    if (corporationDetail.contractType !== Constant.CORPORATION_CONTRACT_TYPE.PREMIUM) {
        return false;
    }

    // Parse corporationSetting and limitValue (they are JSON strings)
    const corporationLimitValue = Utils.convertJSONDataToObject(corporationDetail?.limitValue);
    const corporationSetting = Utils.convertJSONDataToObject(corporationDetail?.corporationSetting);

    // Check mcpServer limitValue
    if (corporationLimitValue?.mcpServer === Constant.STATUS_SERVICE_PERMISSION.OFF) {
        return false;
    }

    // Check usingMCPServer setting
    if (corporationSetting?.usingMCPServer === Constant.STATUS_SERVICE_PERMISSION.OFF) {
        return false;
    }


    return true;
};

const isCanUsingMCPService = (corporationDetail, projectDetail) => {
    const isEnableInOrg = isEnableSettingMCPInOrg(corporationDetail);
    if (!isEnableInOrg) return false;

    if (!projectDetail || projectDetail.active !== Constant.ACTIVE_FLAG.ACTIVE) return false;
    return ![Constant.PROJECT_TYPE.BASE, Constant.PROJECT_TYPE.CUSTOMER].includes(projectDetail.type);
}

const isEnableSettingFileTranslationAttachment = (corporationDetail, projectDetail) => {
    const isGuest = [Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(corporationDetail.contractType)
    const isB2C = projectDetail?.type === Constant.PROJECT_TYPE.CUSTOMER;
    const isBaseProject = projectDetail?.type === Constant.PROJECT_TYPE.BASE;
    const corpPermissionOnly = (isGuest || isB2C || isBaseProject);
    const projectSetting = Utils.convertJSONDataToObject(projectDetail.projectSetting);

    const translateFilePermission = Boolean(isEnableSettingFileTranslation(corporationDetail))
    const multilangPermission = Boolean(isEnableSettingMultiLanguage(corporationDetail))
    const projectPermission = (corpPermissionOnly || projectSetting?.usingFileTranslation === true)

    console.log({translateFilePermission, multilangPermission, projectPermission, isGuest, isB2C})
    return translateFilePermission && multilangPermission && projectPermission;    
}

const CorporationUtils = {
    // AI Chat
    isCorporationOnAI,

    // Knowledge
    isCorporationOnKnowledge,
    checkUserPermissionKnowledgeService,

    // AI Auto
    isCorporationOnAIAuto,

    // SD Auto
    isCorporationOnSDAuto,

    // KBoard
    isCorporationOnKBoardPrompt,

    // Telephone Agent
    isEnableSettingTelephoneAgentInOrg,
    isEnableUsingTelephoneMode,
    isProjectOnTelephoneSupport,
    isProjectOnPhoneSupport,
    shouldShowTrainingView,

    // Email Response
    isEnableCreateEmailTicketMode,

    // MFA
    isCorporationOnMfa,

    // Convert PDF
    isCorpOnConvertPdf,

    // Synonym Search
    isOnSearchSynonym,

    // Desk Service
    isDisabledTicketExternalWhenDeskServiceOff,

    // LimitValue Utils
    customLimitValueBeforeEditOrg,
    convertBigIntTypeInLimitValue,

    // FileTranslation
    isEnableSettingFileTranslation,

    // MCP server
    isEnableSettingMCPInOrg,
    isCanUsingMCPService,

    // Multi Language
    isEnableSettingMultiLanguage,
    isEnableTranslationManagement,
    resolveDictionaryOrgId,
    getDictionaryCacheResetAt,
    isEnableSettingFileTranslationAttachment,
};

export default CorporationUtils;
